/* eslint-disable */

import {
  CAMERA_MIN,
  CAMERA_MAX,
  CAMERA_POSITION,
  CAMERA_LOADING_POSITION,
  CAMERA_DISTANCE_MIN,
  CAMERA_DISTANCE_MAX,
  COIN_HEIGHT,
  COIN_TESSELLATION,
  SMOOTH_FLIP_COEFF,
  SMOOTH_ZOOM_COEFF,
  LIGHT_POSITION,
  LIGHT_GROUND_COLOR,
  LIGHT_SKY_COLOR,
  LIGHT_INTENSITY,
  LOADING_MAT,
  DEFAULT_MAT,
} from '../constant.js'

import { default as MatGold } from './materials/gold.json'
import { default as MatSilver } from './materials/silver.json'
import { LoadingQueue, ease, oscillateXY } from './renderer-utils.js'

import * as BABYLON from '@babylonjs/core'
// import { Scene, ArcRotateCamera, HemisphericLight, Color3, Color4, Vector3, Vector4, Engine, MeshBuilder } from '@babylonjs/core'
// const BABYLON = { Scene, ArcRotateCamera, HemisphericLight, Color3, Color4, Vector3, Vector4, Engine, MeshBuilder }

const state = {
  loading: false,
  intro: false,
  debug: false,
  oscillationEnabled: false,
};

const handlers = {
  onReady() { },
  onCoinLoaded() { },
  onIntroComplete() { },
  onRendered() { }
};

const MIPMAP = false;
const DELTA_FPS_MAX = 1000 / 60;
const DELTA_FPS_MIN = 1000 / 20;

let canvas;
let engine;
let targetRotation = 0;
let targetRotationSmoothCoeff = 1;
let targetOffset = [0, 0];
let world = {};
let staticPath = '/';
let skipFrame = false;

let optimizer;

/** @private */

function createWorld() {
  const scene = new BABYLON.Scene(engine)
  const camera = new BABYLON.ArcRotateCamera('worldCamera', 0, 0, 0, new BABYLON.Vector3(CAMERA_LOADING_POSITION.x, CAMERA_LOADING_POSITION.y, CAMERA_LOADING_POSITION.z))
  const light = new BABYLON.HemisphericLight('worldLight', new BABYLON.Vector3(LIGHT_POSITION.x, LIGHT_POSITION.y, LIGHT_POSITION.z), scene)

  scene.autoClear = false
  scene.clearColor = new BABYLON.Color4(255, 255, 255, 0)

  camera.setTarget(BABYLON.Vector3.Zero())
  camera.minZ = CAMERA_MIN
  camera.maxZ = CAMERA_MAX
  camera.lowerRadiusLimit = CAMERA_DISTANCE_MIN
  camera.upperRadiusLimit = CAMERA_DISTANCE_MAX
  camera.attachControl(canvas, true)
  camera.customPosition = {
    x: camera.position.x,
    y: camera.position.y,
    z: camera.position.z,
  }
  camera.offsetX = 0
  camera.offsetY = 0

  light.diffuse = new BABYLON.Color3(...LIGHT_SKY_COLOR)
  light.groundColor = new BABYLON.Color3(...LIGHT_GROUND_COLOR)

  light.direction = BABYLON.Vector3.Up()
  light.intensity = LIGHT_INTENSITY * 0.5 // /*LIGHT_INTENSITY * 0.000015 /*/ LIGHT_INTENSITY * 0.0015
  // light.specular =      new BABYLON.Color3(0, 0, 255)
  // light.intensityMode = BABYLON.INTENSITYMODE_LUMINOUSPOWER
  // light.falloffType =   BABYLON.FALLOFF_PHYSICAL

  return { scene, camera, light }
}

function updateCamera() {
  state.loading
    ? ease(world.camera.customPosition, 'z', { target: CAMERA_LOADING_POSITION.z, smoothCoeff: 1 })
    : ease(world.camera.customPosition, 'z', { target: CAMERA_POSITION.z, smoothCoeff: SMOOTH_ZOOM_COEFF })

  ease(world.camera, 'offsetX', { target: targetOffset[0], smoothCoeff: 0.15 })
  ease(world.camera, 'offsetY', { target: targetOffset[1], smoothCoeff: 0.15 })

  world.camera.customPosition.x = world.camera.offsetX
  world.camera.customPosition.y = world.camera.offsetY

  if (state.oscillationEnabled) {
    oscillateXY((x, y) => {
      world.camera.customPosition.x += x * 0.25
      world.camera.customPosition.y += y * 0.25
    })
  }

  world.camera.setPosition(new BABYLON.Vector3(world.camera.customPosition.x, world.camera.customPosition.y, world.camera.customPosition.z))
}

function updateCoin() {
  if (state.loading) {
    ease(world.coin.rotation, 'y', { velocity: 0.25 })
    ease(world.coin.material, 'alpha', { target: 0.75, velocity: 0.05 })
    return
  }

  if (state.intro) {
    if (Math.abs(world.coin.rotation.y - targetRotation) < 0.25) onIntroComplete()
  }

  ease(world.coin.rotation, 'y', {
    target: targetRotation,
    smoothCoeff: targetRotationSmoothCoeff,
  })
}

function updateDummy() {
  world.dummy && world.dummy.position.set(world.camera.offsetX, world.camera.offsetY, 0)
}

function getMaterialFromJSON(json, onComplete = () => { }) {
  const replaceStaticPath = (path) => path.replace(/(%STATICS%)\/*/g, staticPath)
  const loadingQueue = new LoadingQueue(() => { onComplete(mat) })
  const mat = json.type && json.type === 'PBR'
    ? new BABYLON.PBRMaterial(json.name, world.scene)
    : new BABYLON.StandardMaterial(json.name, world.scene)

  for (let prop in json) {
    switch (prop) {
      case 'name':
      case 'type':
        break
      case 'normalMap':
        loadingQueue.add()
        mat.bumpTexture = new BABYLON.Texture(replaceStaticPath(json.normalMap, staticPath), world.scene, !MIPMAP)
        mat.bumpTexture.onLoadObservable.add(() => { loadingQueue.remove() })
        break
      case 'envMap':
        loadingQueue.add()
        mat.reflectionTexture = (json.envMap.match(/.*\.env$/i))
          ? new BABYLON.CubeTexture(replaceStaticPath(json.envMap, staticPath), world.scene)
          : new BABYLON.HDRCubeTexture(replaceStaticPath(json.envMap, staticPath), world.scene, 128, false, true, false, true)
        mat.reflectionTexture.onLoadObservable.add(() => { loadingQueue.remove() })
        break
      case 'roughnessMap':
        loadingQueue.add()
        mat.metallicTexture = new BABYLON.Texture(replaceStaticPath(json.roughnessMap, staticPath), world.scene)
        mat.metallicTexture.onLoadObservable.add(() => { loadingQueue.remove() })
        break
      case 'albedoColor':
      case 'emissiveColor':
      case 'specularColor':
        mat[prop] = new BABYLON.Color3(...json[prop])
        break
      case 'metallic':
        mat.metallic = typeof json.metallic === 'number' ? (json.metallic > 0) : json.metallic
        break
      default:
        (typeof mat[prop] !== 'undefined')
          ? mat[prop] = json[prop]
          : console.warn(`renderer-babylon ignore material attribute ${prop}`)
    }
  }

  mat.invertNormalMapX = true
  mat.invertNormalMapY = true

  /*
    mat.enableSpecularAntiAliasing = false
    mat.backFaceCulling = true
    mat.cameraExposure = 2.8
    mat.cameraContrast = 0.8
    mat.disableLighting = true
  */

  loadingQueue.check();

  return mat
}

function onFinalMaterialReady(material) {
  world.material = material
  world.coin.material = world.material

  const nearStepAngle = world.coin.rotation.y - world.coin.rotation.y % (Math.PI * 2) + 4 * Math.PI
  targetRotation = nearStepAngle
  targetRotationSmoothCoeff = 1 / (targetRotation - world.coin.rotation.y) * 0.4

  state.loading = false
  state.intro = true

  handlers.onCoinLoaded()
}

function onIntroComplete() {
  state.intro = false
  handlers.onIntroComplete()
}

function restartOptimizer() {
  optimizer.stop();
  optimizer.reset();
  optimizer.start();
  // BABYLON.SceneOptimizer.OptimizeAsync(world.scene, BABYLON.SceneOptimizerOptions.ModerateDegradationAllowed(),
  //   function () {
  //     // On success
  //     console.info('success');
  //   }, function () {
  //     console.info('not reached');
  //     // FPS target not reached
  //   });
}

/** @public */

export function init(onReady, options = {}, canvasRef=null) {
  if (typeof BABYLON === 'undefined') {
    console.warn('Babylon.js is not available')
    return
  }

  staticPath = options.staticPath || staticPath
  skipFrame = options.skipFrame || false;
  handlers.onReady = options.onReady || onReady || handlers.onReady
  handlers.onCoinLoaded = options.onCoinLoaded || handlers.onCoinLoaded
  handlers.onIntroComplete = options.onIntroComplete || handlers.onIntroComplete
  handlers.onRendered = options.onRendered || handlers.onRendered

  canvas = canvasRef || document.querySelector('#canvas-renderer')
  engine = new BABYLON.Engine(canvas, true, { stencil: true })
  engine.setHardwareScalingLevel(0.5)
  const optOptions = new BABYLON.SceneOptimizerOptions(30, 2000)
  optOptions.addOptimization(new BABYLON.HardwareScalingOptimization(0, 1, 0.25))

  window.engine = engine;

  world = createWorld()

  optimizer = new BABYLON.SceneOptimizer(world.scene, optOptions);
  optimizer.onSuccessObservable.add(function (optim) {
    if (state.debug) console.info('optimization done : scaling level =', engine.getHardwareScalingLevel())
  })
  optimizer.onNewOptimizationAppliedObservable.add(function (optim) {
    if (state.debug) console.info('optimization applied : ', optim.getDescription())
  })
  optimizer.onFailureObservable.add(function () {
    if (state.debug) console.info('optimization State: Failed. Frame rate was' + optimizer.currentFrameRate)
  })

  targetRotation = 0
  targetOffset = [0, 0]
}

let _lastRenderCallTimestamp = 0
let _deltaRenderCall = 0
export function render() {
  _deltaRenderCall = window.performance.now() - _lastRenderCallTimestamp

  if (_deltaRenderCall < DELTA_FPS_MAX) { return }

  _lastRenderCallTimestamp += _deltaRenderCall

  if (!state.debug) {
    updateCamera()
    updateCoin()
    updateDummy()
  }

  if (skipFrame && _deltaRenderCall > DELTA_FPS_MIN) { return }

  world.scene.render();
  handlers.onRendered();
}

export function stopRendering() {
  engine.stopRenderLoop(render);
}

export function startRendering() {
  stopRendering();
  engine.runRenderLoop(render);
}

export function updateSize() {
  //restartOptimizer();
  engine.resize()
}

export function addDummy() {
  //
}

/** @param materialName - silver | gold */
export function addCoin(materialName = 'silver', normalMap = null) {
  const loadingMaterialJSON = { ...LOADING_MAT, alpha: -1 }
  const finalMaterialJSON = { ...DEFAULT_MAT, ...(materialName === 'gold' ? MatGold : MatSilver), normalMap }

  // create loading material
  const loadingMaterial = getMaterialFromJSON(loadingMaterialJSON)

  // load final material
  getMaterialFromJSON(finalMaterialJSON, onFinalMaterialReady)

  // init mesh with loading material
  const faceUV = []
  faceUV[0] = new BABYLON.Vector4(1, 0, 0.5, 1) // x, z swapped to flip image
  faceUV[1] = new BABYLON.Vector4(0, 0, 0, 0)
  faceUV[2] = new BABYLON.Vector4(0, 0, 0.5, 1)

  const mesh = BABYLON.MeshBuilder.CreateCylinder('coin', { height: COIN_HEIGHT, diameter: 1, tessellation: COIN_TESSELLATION, faceUV }, world.scene)
  mesh.material = loadingMaterial
  mesh.rotation.x = -Math.PI * 0.5

  // update world
  world.coin = mesh
  world.material = mesh.material
  state.loading = true

  // dispatch init coin finish
  handlers.onReady()
}

export function setCamOffset(x, y) {
  targetOffset = [x, y]
}

export function flipCoin() {
  world.coin.rotation.y = targetRotation % (Math.PI * 2)
  targetRotation = world.coin.rotation.y + Math.PI
  targetRotationSmoothCoeff = SMOOTH_FLIP_COEFF
}

export function setMaterialFromJSON(json, onComplete) {
  const mat = getMaterialFromJSON(json, onComplete)
  world.material = mat
  world.coin.material = world.material
}

export function setEnvironment(path) {
  world.material.reflectionTexture = new BABYLON.HDRCubeTexture(path, world.scene, 128, false, true, false, true);
}

export function setNormalMap(path) {
  world.material.bumpTexture = new BABYLON.Texture(path, world.scene, !MIPMAP)
}

export function dispose(callback) {
  if (typeof callback !== 'undefined') engine.onDisposeObservable.add(callback)
  engine.dispose()
}

export function setDebugMode(value = true) {
  state.debug = value
}

export function enableOscillation(value = true) {
  state.oscillationEnabled = value
}
